import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  Award, 
  CheckCircle, 
  XCircle, 
  Loader2, 
  Calendar, 
  MapPin, 
  User, 
  BookOpen,
  Shield,
  Home,
  AlertCircle
} from 'lucide-react';
import api from '../lib/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VerifyCertificate = () => {
  const { certificateId } = useParams();
  const [loading, setLoading] = useState(true);
  const [verificationResult, setVerificationResult] = useState(null);

  useEffect(() => {
    const verifyCertificate = async () => {
      try {
        const response = await api.get(`/verify/certificate/${certificateId}`);
        setVerificationResult(response.data);
      } catch (error) {
        setVerificationResult({
          valid: false,
          error: 'Verification failed',
          message: 'Unable to verify this certificate. Please try again later.'
        });
      } finally {
        setLoading(false);
      }
    };

    if (certificateId) {
      verifyCertificate();
    }
  }, [certificateId]);

  const getTierStyles = (tier) => {
    switch (tier?.toLowerCase()) {
      case 'gold':
        return {
          bg: 'bg-gradient-to-br from-yellow-400 via-yellow-500 to-amber-600',
          text: 'text-yellow-700',
          badge: 'bg-yellow-100 text-yellow-800 border-yellow-300',
          icon: '🥇',
          shadow: 'shadow-yellow-200'
        };
      case 'silver':
        return {
          bg: 'bg-gradient-to-br from-slate-300 via-slate-400 to-slate-500',
          text: 'text-slate-700',
          badge: 'bg-slate-100 text-slate-800 border-slate-300',
          icon: '🥈',
          shadow: 'shadow-slate-200'
        };
      default: // bronze
        return {
          bg: 'bg-gradient-to-br from-orange-400 via-orange-500 to-orange-700',
          text: 'text-orange-700',
          badge: 'bg-orange-100 text-orange-800 border-orange-300',
          icon: '🥉',
          shadow: 'shadow-orange-200'
        };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-teal-600 mx-auto mb-4" />
          <p className="text-slate-600">Verifying certificate...</p>
        </div>
      </div>
    );
  }

  if (!verificationResult?.valid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full shadow-xl border-red-200">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Certificate Not Verified</h1>
            <p className="text-slate-600 mb-6">{verificationResult?.message}</p>
            <div className="p-4 bg-red-50 rounded-lg mb-6">
              <div className="flex items-center gap-2 text-red-700">
                <AlertCircle className="w-5 h-5" />
                <span className="font-medium">{verificationResult?.error}</span>
              </div>
            </div>
            <Link to="/">
              <Button className="w-full">
                <Home className="w-4 h-4 mr-2" />
                Go to Homepage
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { certificate, academy } = verificationResult;
  const tierStyles = getTierStyles(certificate?.tier);

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Verification Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-full mb-4">
            <CheckCircle className="w-5 h-5" />
            <span className="font-semibold">Certificate Verified</span>
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Authentic Certificate</h1>
          <p className="text-slate-600">This certificate has been verified and is authentic.</p>
        </div>

        {/* Certificate Card */}
        <Card className={`shadow-2xl ${tierStyles.shadow} border-2 overflow-hidden`}>
          {/* Tier Badge Banner */}
          <div className={`${tierStyles.bg} text-white py-4 px-6`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-3xl">{tierStyles.icon}</span>
                <div>
                  <p className="text-sm opacity-90">Achievement Level</p>
                  <p className="text-xl font-bold uppercase">{certificate?.tier || 'Bronze'} Certificate</p>
                </div>
              </div>
              <Shield className="w-10 h-10 opacity-80" />
            </div>
          </div>

          <CardContent className="p-8">
            {/* Academy Header */}
            <div className="text-center mb-8 pb-6 border-b border-dashed">
              <div className="flex justify-center items-center gap-4 mb-4">
                {academy?.logo_url ? (
                  <img src={academy.logo_url.startsWith('http') ? academy.logo_url : `${BACKEND_URL}${academy.logo_url}`} alt="Logo" className="w-16 h-16 object-contain" />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center">
                    <Award className="w-8 h-8 text-teal-600" />
                  </div>
                )}
                {certificate?.school_logo_url && (
                  <>
                    <span className="text-slate-300 text-2xl">+</span>
                    <img src={certificate.school_logo_url.startsWith('http') ? certificate.school_logo_url : `${BACKEND_URL}${certificate.school_logo_url}`} alt="School Logo" className="w-16 h-16 object-contain" />
                  </>
                )}
              </div>
              <h2 className="text-xl font-bold text-teal-700">{academy?.name}</h2>
              <p className="text-slate-500">{academy?.subtitle}</p>
              {certificate?.school_name && (
                <p className="text-sm text-slate-600 mt-2">
                  In collaboration with <span className="font-semibold">{certificate.school_name}</span>
                </p>
              )}
            </div>

            {/* Certificate Content */}
            <div className="text-center mb-8">
              <p className="text-slate-500 text-sm uppercase tracking-wider mb-2">Certificate of Completion</p>
              <h3 className="text-3xl font-bold text-slate-900 mb-4">{certificate?.student_name}</h3>
              <p className="text-slate-600 mb-2">has successfully completed the</p>
              <p className="text-xl font-semibold text-teal-700 mb-6">{certificate?.program_name}</p>
            </div>

            {/* Details Grid */}
            <div className="grid grid-cols-2 gap-4 mb-8">
              <div className="p-4 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                  <User className="w-4 h-4" />
                  Instructor
                </div>
                <p className="font-semibold text-slate-900">{certificate?.coach_name || 'N/A'}</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                  <MapPin className="w-4 h-4" />
                  Location
                </div>
                <p className="font-semibold text-slate-900">{certificate?.location_name || 'N/A'}</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                  <BookOpen className="w-4 h-4" />
                  Lessons Completed
                </div>
                <p className="font-semibold text-slate-900">{certificate?.total_lessons || 'N/A'}</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
                  <Calendar className="w-4 h-4" />
                  Issue Date
                </div>
                <p className="font-semibold text-slate-900">
                  {certificate?.approved_at ? new Date(certificate.approved_at).toLocaleDateString() : 'N/A'}
                </p>
              </div>
            </div>

            {/* Program Dates */}
            {(certificate?.program_start_date || certificate?.program_end_date) && (
              <div className="text-center mb-6 p-4 bg-teal-50 rounded-xl">
                <p className="text-sm text-teal-700">
                  Program Period: {certificate.program_start_date || 'N/A'} — {certificate.program_end_date || 'N/A'}
                </p>
              </div>
            )}

            {/* Certificate ID & Approval */}
            <div className="border-t border-dashed pt-6">
              <div className="flex justify-between items-center text-sm">
                <div>
                  <p className="text-slate-500">Certificate ID</p>
                  <p className="font-mono font-semibold text-slate-700">{certificate?.id?.slice(0, 8).toUpperCase()}</p>
                </div>
                <div className="text-right">
                  <p className="text-slate-500">Approved by</p>
                  <p className="font-semibold text-slate-700">{certificate?.approved_by || 'Administrator'}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Back to Homepage */}
        <div className="text-center mt-8">
          <Link to="/">
            <Button variant="outline" className="gap-2">
              <Home className="w-4 h-4" />
              Visit Eswatini Seals
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default VerifyCertificate;
